import React, { useEffect, useState } from 'react';
import axios from 'axios';
import MathText from '../components/MathText';

const API = process.env.REACT_APP_API_URL || '';

// ─── ID parsing ───────────────────────────────────────────────────────────────
// All IDs now follow a single standard pattern: pt{N}_{section}{mod}_q{num}
//   e.g. pt11_rw1_q3,  pt6_math2_q10,  pt3_rw1_q27
function getPTInfo(id) {
  // Matches pt11_rw1_q*, pt6_math2_q*, pt3_rw2_q*, etc.
  const m = id.match(/^pt(\d+)_(rw|math)(\d+)_q/);
  if (m) return { ptNum: parseInt(m[1], 10), module: parseInt(m[3], 10) };
  // Matches ng1_rw1_q*, ng1_math2_q*, etc.
  const ng = id.match(/^ng(\d+)_(rw|math)(\d+)_q/);
  if (ng) return { ptNum: `ng${ng[1]}`, module: parseInt(ng[3], 10) };
  return { ptNum: 99, module: 1 };
}

function getQNum(id) {
  const m = id.match(/_q(\d+)$/);
  return m ? parseInt(m[1], 10) : 0;
}

const PT_ORDER   = [3, 4, 5, 6, 7, 8, 9, 10, 11, 'ng1', 'ng2', 'ng3', 'ng4', 'ng5', 'ng6', 'ng7', 'ng8', 'ng9', 'ng10', 'ng11', 'ng12', 'ng13', 'ng14', 'ng15', 'ng16', 'ng17', 'ng18', 'ng19', 'ng20', 'ng21', 'ng22', 'ng23', 'ng24', 'ng25', 'ng26', 'ng27', 'ng28', 'ng29', 'ng30', 'ng31', 'ng32', 'ng33', 'ng34', 'ng35', 'ng36', 'ng37', 'ng38', 'ng39', 'ng40'];
const SUBJECTS   = ['Reading & Writing', 'Math'];

const AUDIT_PASSWORD = 'a16zhateswatermelons';

function PasswordGate({ onUnlock }) {
  const [input, setInput] = useState('');
  const [shake, setShake] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    if (input === AUDIT_PASSWORD) {
      sessionStorage.setItem('auditUnlocked', '1');
      onUnlock();
    } else {
      setShake(true);
      setInput('');
      setTimeout(() => setShake(false), 600);
    }
  }

  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--cb-white)',
        border: '1px solid var(--cb-gray-200)',
        borderRadius: 14,
        padding: '36px 40px',
        boxShadow: 'var(--shadow-sm)',
        width: '100%',
        maxWidth: 360,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--cb-gray-900)', margin: '0 0 6px' }}>
          Audit Access
        </h2>
        <p style={{ fontSize: 13, color: 'var(--cb-gray-500)', marginBottom: 24 }}>
          Enter the password to continue.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            autoFocus
            type="password"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Password"
            style={{
              width: '100%',
              padding: '10px 14px',
              fontSize: 14,
              border: '1.5px solid var(--cb-gray-200)',
              borderRadius: 8,
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: 12,
              animation: shake ? 'shake 0.5s' : 'none',
            }}
          />
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', padding: '10px 0', fontSize: 14 }}
          >
            Unlock
          </button>
        </form>
        <style>{`
          @keyframes shake {
            0%,100%{transform:translateX(0)}
            20%{transform:translateX(-8px)}
            40%{transform:translateX(8px)}
            60%{transform:translateX(-6px)}
            80%{transform:translateX(6px)}
          }
        `}</style>
      </div>
    </div>
  );
}

export default function Audit() {
  const [unlocked, setUnlocked]   = useState(() => sessionStorage.getItem('auditUnlocked') === '1');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [activeTab, setActiveTab] = useState(3);       // PT number
  const [activeSubject, setActiveSubject] = useState('Reading & Writing');

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  useEffect(() => {
    axios.get(`${API}/api/audit/all-questions`)
      .then(res => {
        setQuestions(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(err => {
        setError(err?.response?.data?.error || err.message || 'Unknown error');
        setLoading(false);
      });
  }, []);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--cb-gray-500)' }}>
      Loading questions…
    </div>
  );
  if (error) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--cb-red)' }}>
      <strong>Error:</strong> {error}
    </div>
  );

  // Group all questions by PT number + section
  const byPT = {};
  PT_ORDER.forEach(pt => {
    byPT[pt] = { 'Reading & Writing': [], Math: [] };
  });

  questions.forEach(q => {
    const { ptNum } = getPTInfo(q.id);
    const section   = q.section === 'Math' ? 'Math' : 'Reading & Writing';
    if (byPT[ptNum]) byPT[ptNum][section].push(q);
  });

  // Sort within each bucket: mod 1 first, then by q number
  PT_ORDER.forEach(pt => {
    SUBJECTS.forEach(subj => {
      byPT[pt][subj].sort((a, b) => {
        const ia = getPTInfo(a.id), ib = getPTInfo(b.id);
        if (ia.module !== ib.module) return ia.module - ib.module;
        return getQNum(a.id) - getQNum(b.id);
      });
    });
  });

  const tabQs = byPT[activeTab]?.[activeSubject] || [];
  const mod1  = tabQs.filter(q => getPTInfo(q.id).module === 1);
  const mod2  = tabQs.filter(q => getPTInfo(q.id).module === 2);

  const rwCount   = byPT[activeTab]?.['Reading & Writing']?.length ?? 0;
  const mathCount = byPT[activeTab]?.['Math']?.length ?? 0;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 20px 80px' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--cb-gray-900)', margin: 0 }}>
          Question Audit
        </h1>
        <div style={{ color: 'var(--cb-gray-500)', fontSize: 13, marginTop: 4 }}>
          {questions.length} total questions
        </div>
      </div>

      {/* ── Practice test tabs ── */}
      <div style={{
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        borderBottom: '2px solid var(--cb-gray-200)',
        marginBottom: 0,
      }}>
        {PT_ORDER.map(pt => {
          const active = pt === activeTab;
          const total  = (byPT[pt]?.['Reading & Writing']?.length ?? 0) +
                         (byPT[pt]?.['Math']?.length ?? 0);
          return (
            <button
              key={pt}
              onClick={() => setActiveTab(pt)}
              style={{
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                border: 'none',
                borderBottom: active ? '3px solid var(--cb-blue)' : '3px solid transparent',
                background: 'none',
                color: active ? 'var(--cb-blue)' : 'var(--cb-gray-500)',
                cursor: 'pointer',
                marginBottom: -2,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              {typeof pt === 'string' ? pt.toUpperCase() : `PT ${pt}`}
              <span style={{
                background: active ? 'var(--cb-blue-light)' : 'var(--cb-gray-100)',
                color: active ? 'var(--cb-blue)' : 'var(--cb-gray-400)',
                borderRadius: 10,
                padding: '1px 6px',
                fontSize: 11,
                fontWeight: 600,
              }}>
                {total}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Subject subtabs ── */}
      <div style={{
        display: 'flex',
        gap: 0,
        background: 'var(--cb-gray-100)',
        border: '1px solid var(--cb-gray-200)',
        borderTop: 'none',
        borderRadius: '0 0 10px 10px',
        padding: '8px 12px',
        marginBottom: 24,
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 6,
      }}>
        {SUBJECTS.map(subj => {
          const active = subj === activeSubject;
          const count  = subj === 'Reading & Writing' ? rwCount : mathCount;
          return (
            <button
              key={subj}
              onClick={() => setActiveSubject(subj)}
              style={{
                padding: '5px 14px',
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                border: active ? '1.5px solid var(--cb-blue)' : '1px solid var(--cb-gray-200)',
                borderRadius: 20,
                background: active ? 'var(--cb-blue)' : 'var(--cb-white)',
                color: active ? '#fff' : 'var(--cb-gray-600)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {subj}
              <span style={{
                background: active ? 'rgba(255,255,255,0.25)' : 'var(--cb-gray-100)',
                color: active ? '#fff' : 'var(--cb-gray-500)',
                borderRadius: 10,
                padding: '0px 6px',
                fontSize: 11,
                fontWeight: 600,
              }}>
                {count}
              </span>
            </button>
          );
        })}
        <span style={{ marginLeft: 8, color: 'var(--cb-gray-400)', fontSize: 12 }}>
          {tabQs.length} question{tabQs.length !== 1 ? 's' : ''} · {typeof activeTab === 'string' ? activeTab.toUpperCase() : `PT ${activeTab}`}
        </span>
      </div>

      {/* ── Questions ── */}
      {tabQs.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--cb-gray-500)' }}>
          No {activeSubject} questions for {typeof activeTab === 'string' ? activeTab.toUpperCase() : `PT ${activeTab}`}.
        </div>
      ) : (
        <>
          {mod1.length > 0 && (
            <>
              <ModuleHeader label="Module 1" count={mod1.length} />
              {mod1.map((q, i) => <QuestionCard key={q.id} q={q} num={i + 1} />)}
            </>
          )}
          {mod2.length > 0 && (
            <>
              <ModuleHeader label="Module 2" count={mod2.length} />
              {mod2.map((q, i) => <QuestionCard key={q.id} q={q} num={i + 1} />)}
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Module divider ─────────────────────────────────────────────────────────── */
function ModuleHeader({ label, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 18px' }}>
      <div style={{
        background: 'var(--cb-blue)',
        color: '#fff',
        fontWeight: 700,
        fontSize: 12,
        padding: '4px 12px',
        borderRadius: 20,
        letterSpacing: 0.3,
      }}>
        {label}
      </div>
      <span style={{ color: 'var(--cb-gray-400)', fontSize: 13 }}>{count} questions</span>
      <div style={{ flex: 1, height: 1, background: 'var(--cb-gray-200)' }} />
    </div>
  );
}

/* ─── Single question card ───────────────────────────────────────────────────── */
function QuestionCard({ q, num }) {
  const isFR = q.isFreeResponse ||
    (q.choices && Object.values(q.choices).every(v => v === 'N/A'));

  return (
    <div style={{
      background: 'var(--cb-white)',
      border: '1px solid var(--cb-gray-200)',
      borderRadius: 12,
      padding: '20px 22px',
      marginBottom: 20,
      boxShadow: 'var(--shadow-sm)',
    }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--cb-gray-900)', minWidth: 24 }}>
          {num}.
        </span>
        <code style={{
          background: 'var(--cb-gray-100)',
          color: 'var(--cb-gray-500)',
          padding: '2px 7px',
          borderRadius: 5,
          fontSize: 11,
        }}>
          {q.id}
        </code>
        {q.skill && (
          <span style={{
            background: 'var(--cb-blue-light)',
            color: 'var(--cb-blue)',
            padding: '2px 8px',
            borderRadius: 5,
            fontSize: 11,
            fontWeight: 600,
          }}>
            {q.skill}
          </span>
        )}
        {isFR && (
          <span style={{
            background: '#fef3c7',
            color: '#b45309',
            padding: '2px 8px',
            borderRadius: 5,
            fontSize: 11,
            fontWeight: 600,
          }}>
            Free Response
          </span>
        )}
      </div>

      {/* Passage — gray box */}
      {q.passage && (
        <div style={{
          background: 'var(--cb-gray-100)',
          border: '1px solid var(--cb-gray-200)',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 14,
          fontSize: 14,
          lineHeight: 1.75,
          color: 'var(--cb-gray-900)',
          whiteSpace: 'pre-wrap',
        }}>
          <MathText text={q.passage} />
        </div>
      )}

      {/* Figure */}
      {(q.figure || (q.figures && q.figures.length > 0)) && (
        <div style={{ marginBottom: 14 }}>
          {(q.figures ? q.figures : [q.figure]).map((fig, i) => fig && (
            <img
              key={i}
              src={`/figures/${fig}`}
              alt={`Figure for ${q.id}`}
              onError={e => {
                e.target.style.outline = '2px dashed red';
                e.target.alt = `MISSING: /figures/${fig}`;
              }}
              style={{
                maxWidth: '100%',
                borderRadius: 8,
                border: '1px solid var(--cb-gray-200)',
                display: 'block',
                marginBottom: 8,
              }}
            />
          ))}
        </div>
      )}

      {/* Question stem */}
      <div style={{
        fontSize: 15,
        lineHeight: 1.7,
        color: 'var(--cb-gray-900)',
        marginBottom: 16,
        fontWeight: 500,
      }}>
        <MathText text={q.question} />
      </div>

      {/* Choices or free-response */}
      {isFR ? (
        <div style={{
          padding: '10px 14px',
          borderRadius: 8,
          border: '1px solid var(--cb-gray-200)',
          background: '#f0fdf4',
          fontSize: 14,
          color: 'var(--cb-gray-700)',
          marginBottom: 14,
        }}>
          <strong>Answer: </strong>
          <span style={{ color: 'var(--cb-green)', fontWeight: 700 }}>
            {q.freeResponseAnswer ?? q.correctAnswer ?? '—'}
          </span>
        </div>
      ) : q.choices ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 14 }}>
          {Object.entries(q.choices).map(([letter, text]) => {
            const isCorrect = letter === q.correctAnswer;
            return (
              <div key={letter} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '9px 14px',
                borderRadius: 8,
                border: isCorrect ? '1.5px solid var(--cb-green)' : '1px solid var(--cb-gray-200)',
                background: isCorrect ? 'var(--cb-green-light)' : 'var(--cb-gray-100)',
                fontSize: 14,
                lineHeight: 1.5,
              }}>
                <span style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: isCorrect ? 'var(--cb-green)' : 'var(--cb-gray-500)',
                  minWidth: 18,
                  paddingTop: 1,
                  flexShrink: 0,
                }}>
                  {letter}
                </span>
                <span style={{ color: 'var(--cb-gray-900)', flex: 1 }}><MathText text={text} /></span>
                {isCorrect && (
                  <span style={{ color: 'var(--cb-green)', fontWeight: 700, fontSize: 13 }}>✓</span>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Explanation */}
      {q.explanation && (
        <div style={{
          fontSize: 13,
          color: 'var(--cb-gray-500)',
          borderTop: '1px solid var(--cb-gray-200)',
          paddingTop: 10,
          fontStyle: 'italic',
          lineHeight: 1.6,
        }}>
          <strong style={{ fontStyle: 'normal', color: 'var(--cb-gray-700)' }}>Explanation: </strong>
          {q.explanation}
        </div>
      )}
    </div>
  );
}
